<template>
  <div class="relative flex flex-wrap items-center space-x-2">
    <formula-input-toggle
      v-if="initiated"
      v-show="activeItemLevelString === levelString"
      @select="emit('equation-item:add', $event)"
    />
    <component
      :is="formulaItemComponent"
      v-bind="componentProps"
      class="cursor-pointer rounded-sm"
      :class="[
        FORMULA_CLASS_MAP[propName],
        {
          'border border-blue-300': activeItemLevelString === levelString,
        },
      ]"
      @click.stop="
        setActiveLevel(activeItemLevelString === levelString ? [] : level)
      "
    />
    <icon-base
      v-show="activeItemLevelString === levelString"
      v-tooltip="$t('COMMON.DELETE')"
      :icon="IconX"
      class="absolute -top-1 -right-1 cursor-pointer rounded-sm border border-gray-300 bg-disabled hover:border-red-300 hover:text-red-300"
      data-cy="delete-item"
      @click.stop="emit('equation-item:delete')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watchPostEffect } from "vue";

import { storeToRefs } from "pinia";

import { MathOperation } from "@/enums/customAttributes";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import OperationItem from "@/components/customAttributes/OperationItem.vue";
import EquationAttribute from "@/components/customAttributes/formula/EquationAttribute.vue";
import EquationValue from "@/components/customAttributes/formula/EquationValue.vue";
import FormulaEquation from "@/components/customAttributes/formula/FormulaEquation.vue";
import FormulaInputToggle from "@/components/customAttributes/formula/FormulaInputToggle.vue";
import IconX from "@/components/icons/deals/IconX.vue";

import type { MaybeArray } from "@/models/common";
import type { CustomAttributeFormulaItem } from "@/models/customAttributes/customAttribute";
import type { Component, PropType } from "vue";

enum PROPS_NAME {
  OPERATION = "operation",
  VALUE = "value",
  FORMULA = "formula",
}

const FORMULA_COMPONENT_MAP: Record<string, Component> = {
  string: OperationItem,
  number: EquationValue,
  object: EquationAttribute,
};

const FORMULA_CLASS_MAP = {
  [PROPS_NAME.VALUE]: "item-attribute",
  [PROPS_NAME.OPERATION]: "item-operation",
  [PROPS_NAME.FORMULA]: undefined,
};

const emit = defineEmits<{
  "equation-item:add": [value: MaybeArray<CustomAttributeFormulaItem>];
  "equation-item:delete": [];
}>();

const props = defineProps({
  item: {
    type: [String, Object, Number, Array] as PropType<
      CustomAttributeFormulaItem | CustomAttributeFormulaItem[]
    >,
    required: true,
  },
  level: {
    type: Array as PropType<number[]>,
    required: true,
  },
});

const initiated = ref(false);

const attributeBuilderStore = useAttributeBuilderStore();
const { activeItemLevelString } = storeToRefs(attributeBuilderStore);
const { setActiveLevel } = attributeBuilderStore;

const isItemFormula = computed(() => Array.isArray(props.item));
const isItemAttribute = computed(
  () => !isItemFormula.value && typeof props.item === "object"
);
const levelString = computed(() => props.level.join());

const formulaItemComponent = computed(() => {
  return isItemFormula.value
    ? FormulaEquation
    : FORMULA_COMPONENT_MAP[typeof props.item];
});

const propName = computed(() => {
  if (isItemFormula.value) {
    return PROPS_NAME.FORMULA;
  } else if (
    Object.values(MathOperation).includes(props.item as MathOperation)
  ) {
    return PROPS_NAME.OPERATION;
  } else {
    return PROPS_NAME.VALUE;
  }
});

const componentProps = computed(() => {
  return {
    [propName.value]: props.item,
    ...(isItemFormula.value && { level: props.level }),
    ...(isItemAttribute.value && {
      isActive: activeItemLevelString.value === levelString.value,
    }),
  };
});

watchPostEffect(() => {
  if (activeItemLevelString.value === levelString.value) {
    initiated.value = true;
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.item-attribute {
  @apply border p-3;
}
.item-operation {
  @apply rounded bg-disabled p-1;
}
</style>
